import Document from "@tiptap/extension-document";
import History from "@tiptap/extension-history";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { useEditor } from "@tiptap/react";
import clsx from "clsx";
import { useEffect } from "react";

import VariableExtension from "features/templates/components/TemplateEditor/VariableExtension";
import type {
  TemplateSchemaType,
  VoiceTemplateSchemaType,
} from "features/templates/schemas";
import {
  parseTemplateText,
  stringifyTemplateContent,
} from "features/templates/utils";

import type { UseFormReturn } from "react-hook-form";
import type { DetailedCampaign } from "../../../pages/campaigns/types";

export default function useTemplateSubjectEditor({
  isEditable,
  form: { getValues, subscribe, setValue, formState },
  customVariables,
  onFocus,
}: {
  isEditable: boolean;
  form: UseFormReturn<Exclude<TemplateSchemaType, VoiceTemplateSchemaType>>;
  customVariables: DetailedCampaign["custom_variable_keys"];
  onFocus: () => void;
}) {
  const initialValue = getValues("subject");

  const templateSubjectEditor = useEditor({
    editable: isEditable,
    extensions: [
      Document.extend({
        content: "block",
      }),
      Paragraph,
      Text,
      History,
      VariableExtension(customVariables),
    ],
    content: initialValue
      ? parseTemplateText(initialValue, customVariables, {})
      : null,
    onUpdate: ({ editor }) => {
      setValue("subject", stringifyTemplateContent(editor.getJSON()), {
        shouldTouch: true,
        shouldValidate: formState.isSubmitted,
        shouldDirty: true,
      });
    },
    onFocus,
    editorProps: {
      attributes: {
        class: clsx(
          "w-full bg-transparent text-md lg:text-sm outline-none [&_.node-variable]:align-middle",
          !isEditable && "text-black-500",
        ),
      },
    },
    shouldRerenderOnTransaction: false,
  });

  useEffect(() => {
    return subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        const editorSubject = stringifyTemplateContent(
          templateSubjectEditor.getJSON(),
        );
        if ("subject" in values && editorSubject !== values.subject) {
          templateSubjectEditor.commands.setContent(
            parseTemplateText(values.subject, customVariables, {}),
          );
        }
      },
    });
  }, [subscribe]);

  return { templateSubjectEditor };
}
